import { apiReminders } from "@/api/reminder.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiReminderHistoryFilter, ApiReminderSend } from "@/types/reminder.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const remindersKeys = createQueryKeys("reminders", {
  history: (filter: ApiReminderHistoryFilter) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiReminders.history(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useRemindersHistory = (filter: ApiReminderHistoryFilter, enabled: boolean = true) => {
  return useQuery({
    ...remindersKeys.history(filter),
    enabled,
  });
};

export const useReminderSend = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ApiReminderSend) => {
      const result = await apiReminders.send(payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: remindersKeys._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
